import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T, typographyCss } from 'common/typography';

type Metric = {
  title: string;
  value: React.ReactNode;
  tooltip?: string;
  isLoading?: boolean;
};

type MetricsBlockProps = {
  title: React.ReactNode;
  headerAction?: React.ReactNode;
  primaryMetrics: Metric[];
  secondaryMetrics: Metric[];
  colored?: boolean;
  isLoading?: boolean;
};

const HeaderRow = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-block-end: 12px;
`;

const PrimaryMetricsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-block-end: 16px;
  gap: 16px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    margin-block-end: 12px;
    gap: 12px;
  }
`;

const MetricsBlockRoot = styled.div`
  padding: 24px;

  border: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  border-radius: 8px;

  display: flex;
  flex-direction: column;
  gap: 4px;

  flex-grow: 1;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`;

const MetricTitle = styled(T.Body3).attrs({ fontWeight: 'semiBold' })`
  color: ${cbSemanticCssVars.textSecondary};
`;

const MetricValue = styled.div<{ colored?: boolean }>`
  ${typographyCss.Heading5};
  font-weight: 600;
  ${props => props.colored && `color: ${colors.wolt100}`};
`;

const SecondaryMetricsRow = styled.div`
  display: flex;
  justify-content: space-between;
  border: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  border-radius: 8px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 16px;
    gap: 20px;
  }
`;

const SecondaryMetricBlockRoot = styled.div`
  padding: 16px 24px;

  display: flex;
  flex-direction: column;
  gap: 4px;

  flex-grow: 1;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding: 0;
  }
`;

const SecondaryMetricTitle = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const SecondaryMetricsValue = styled.div`
  ${typographyCss.Heading6};
  font-weight: 600;
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${cbSemanticCssVars.textSecondary};
  margin-block-end: -1px;
`;

const PopoverContent = styled.div`
  padding: 6px 10px;
  max-width: 256px;

  ${typographyCss.Small};
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledSpinner = styled(Spinner)`
  vertical-align: middle; /* this prevents spinner to change height of the text line */
`;

const TooltipPopover = ({ tooltip }: { tooltip: string }) => (
  <LegacyPopover content={<PopoverContent>{tooltip}</PopoverContent>} disableContentPadding>
    &nbsp;
    <InfoIcon />
  </LegacyPopover>
);

const SecondaryMetricBlock = ({ title, value, tooltip, isLoading }: { colored?: boolean } & Metric) => (
  <SecondaryMetricBlockRoot>
    <SecondaryMetricTitle>
      {title}
      {tooltip && <TooltipPopover tooltip={tooltip} />}
    </SecondaryMetricTitle>
    <SecondaryMetricsValue>{isLoading ? <StyledSpinner size="small" color="grey" /> : value}</SecondaryMetricsValue>
  </SecondaryMetricBlockRoot>
);

const MetricBlock = ({ title, value, colored = true, tooltip, isLoading }: { colored?: boolean } & Metric) => (
  <MetricsBlockRoot>
    <MetricTitle>
      {title}
      {tooltip && <TooltipPopover tooltip={tooltip} />}
    </MetricTitle>
    <MetricValue colored={colored}>{isLoading ? <StyledSpinner size="medium" /> : value}</MetricValue>
  </MetricsBlockRoot>
);

export const MetricsBlock = ({
  title,
  headerAction,
  primaryMetrics,
  secondaryMetrics,
  colored,
  isLoading,
}: MetricsBlockProps) => {
  return (
    <div>
      <HeaderRow>
        <T.Heading6>{title}</T.Heading6>
        {headerAction}
      </HeaderRow>
      <div>
        <PrimaryMetricsRow>
          {primaryMetrics.map(metric => (
            <MetricBlock
              key={metric.title}
              title={metric.title}
              value={metric.value}
              tooltip={metric.tooltip}
              colored={colored}
              isLoading={isLoading}
            />
          ))}
        </PrimaryMetricsRow>
        <SecondaryMetricsRow>
          {secondaryMetrics.map(metric => (
            <SecondaryMetricBlock
              key={metric.title}
              title={metric.title}
              value={metric.value}
              tooltip={metric.tooltip}
              isLoading={isLoading}
            />
          ))}
        </SecondaryMetricsRow>
      </div>
    </div>
  );
};
